/**
 * ApexStackPanel — the seven persistence conditions.
 *
 * Each condition is rendered as an expandable card with its tagline,
 * structural mechanism, failure mode, and the rulebook codes that fire
 * when the condition is violated. The seven together form the apex
 * stack — a claim persists across version evolution iff all seven hold.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useState } from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import apexStackData from "../data/apexStack.json" with { type: "json" };

const ACCENT = "#5eead4";

interface Condition {
  readonly id: string;
  readonly ordinal: number;
  readonly label: string;
  readonly tagline: string;
  readonly mechanism: string;
  readonly failureMode: string;
  readonly rulebookCodes: readonly string[];
}

const CONDITIONS: readonly Condition[] = apexStackData.conditions;

export function ApexStackPanel(): React.JSX.Element {
  const [expandedId, setExpandedId] = useState<string | null>(
    CONDITIONS[0]?.id ?? null,
  );

  return (
    <PanelSurface.PanelShell
      title="The Apex Stack"
      subtitle="Seven persistence conditions — a claim survives evolution iff all hold"
      accentColor={ACCENT}
    >
      <PanelSurface.Card>
        <p className="m-0 text-[14.5px] leading-[1.75] text-[var(--ilg-text)]">
          A static CSK claim is established at a fixed regime{" "}
          <code className="font-mono text-[var(--ilg-bright)]">R</code>. For
          that claim to <em>persist</em> across version evolution, ILG demands
          seven conditions. Each one is structural — its failure does not appear
          as a warning, it produces a refusal with a stable rulebook code. The
          stack is irredundant: dropping any condition reopens at least one of
          the nine A1..A9 attack classes.
        </p>
      </PanelSurface.Card>

      <div className="flex flex-col gap-2.5">
        {CONDITIONS.map((c) => {
          const expanded = c.id === expandedId;
          return (
            <PanelSurface.Card
              key={c.id}
              className="p-0 overflow-hidden"
              style={{
                borderColor: expanded ? `${ACCENT}66` : panelTokens.border,
              }}
              glowColor={expanded ? "rgba(94,234,212,0.08)" : undefined}
            >
              <button
                type="button"
                onClick={() => {
                  setExpandedId(expanded ? null : c.id);
                }}
                aria-expanded={expanded}
                className="w-full flex items-start gap-3.5 px-5 py-4 border-none bg-transparent text-left cursor-pointer text-[var(--ilg-text)]"
              >
                <span
                  className="w-[36px] h-[36px] flex-shrink-0 rounded-[10px] font-mono font-extrabold text-[16px] flex items-center justify-center"
                  style={{
                    background: `${ACCENT}18`,
                    border: `1px solid ${ACCENT}55`,
                    color: ACCENT,
                  }}
                >
                  {c.ordinal}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[15.5px] font-bold text-[var(--ilg-bright)]">
                    {c.label}
                  </span>
                  <span className="block mt-1 text-[12.5px] text-[var(--ilg-muted)] font-mono tracking-[0.04em]">
                    {c.tagline}
                  </span>
                </span>
                <span
                  aria-hidden
                  className="text-[var(--ilg-muted)] font-mono text-[18px] leading-none"
                  style={{
                    transform: expanded ? "rotate(90deg)" : "rotate(0)",
                    transition: "transform 150ms ease",
                  }}
                >
                  ›
                </span>
              </button>
              {expanded && (
                <div className="flex flex-col gap-3.5 pt-0 pr-5 pb-[18px] pl-[70px]">
                  <div>
                    <PanelSurface.SectionHeading>Mechanism</PanelSurface.SectionHeading>
                    <p className="m-0 mt-2 text-[13.5px] leading-[1.7] text-[var(--ilg-text)]">
                      {c.mechanism}
                    </p>
                  </div>
                  <div>
                    <PanelSurface.SectionHeading>Failure mode</PanelSurface.SectionHeading>
                    <p className="m-0 mt-2 text-[13.5px] leading-[1.7] text-[var(--ilg-text)]">
                      {c.failureMode}
                    </p>
                  </div>
                  <div>
                    <PanelSurface.SectionHeading>
                      Rulebook codes that fire on violation
                    </PanelSurface.SectionHeading>
                    <div className="mt-2 flex gap-1.5 flex-wrap">
                      {c.rulebookCodes.map((code) =>
                        PanelSurface.badge(code, panelTokens.accentAmber),
                      )}
                    </div>
                  </div>
                </div>
              )}
            </PanelSurface.Card>
          );
        })}
      </div>
    </PanelSurface.PanelShell>
  );
}
